(() => {
  const selectors = {
    root: '[data-custom-silver-upload]',
    input: '[data-custom-silver-upload-input]',
    preview: '[data-custom-silver-upload-preview]',
    status: '[data-custom-silver-upload-status]',
    error: '[data-custom-silver-upload-error]',
    urls: '[data-custom-silver-upload-urls]',
    state: '[data-custom-silver-upload-state]',
    filenames: '[data-custom-silver-upload-filenames]',
    count: '[data-custom-silver-upload-count]',
    batchId: '[data-custom-silver-upload-batch-id]',
  };

  const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
  const fallbackState = '未上傳，需透過 LINE 補圖';
  const failedState = '上傳失敗，需透過 LINE 補圖';
  const uploadErrorMessage = '圖片暫時無法上傳，請下單後透過官方 LINE 補傳照片與訂單編號。';

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll(selectors.root).forEach(initUpload);
  });

  function initUpload(root) {
    const input = root.querySelector(selectors.input);
    const preview = root.querySelector(selectors.preview);
    const status = root.querySelector(selectors.status);
    const error = root.querySelector(selectors.error);
    const urlsInput = root.querySelector(selectors.urls);
    const stateInput = root.querySelector(selectors.state);
    const filenamesInput = root.querySelector(selectors.filenames);
    const countInput = root.querySelector(selectors.count);
    const batchIdInput = root.querySelector(selectors.batchId);
    const form = root.closest('form');

    if (!input || !preview || !status || !error || !urlsInput || !stateInput || !filenamesInput || !countInput || !batchIdInput) return;

    const maxFiles = Number(root.dataset.maxFiles || 5);
    const maxFileSizeMb = Number(root.dataset.maxFileSizeMb || 10);
    const uploadMode = root.dataset.uploadMode || 'worker';
    const endpoint = root.dataset.uploadEndpoint || root.dataset.createUploadUrlEndpoint || '';
    const uploadBatchId = createUploadBatchId();

    batchIdInput.value = uploadBatchId;
    setFallbackState(urlsInput, stateInput, filenamesInput, countInput);

    input.addEventListener('change', async () => {
      const files = Array.from(input.files || []);
      await handleUpload({
        files,
        input,
        preview,
        status,
        error,
        urlsInput,
        stateInput,
        filenamesInput,
        countInput,
        maxFiles,
        maxFileSizeMb,
        uploadMode,
        endpoint,
        uploadBatchId,
      });
    });

    if (form) {
      form.addEventListener('submit', () => {
        if (!urlsInput.value) {
          setFallbackState(urlsInput, stateInput, filenamesInput, countInput);
          status.textContent = fallbackState;
        }
      });
    }
  }

  function validateFiles(files, maxFiles = 5, maxFileSizeMb = 10) {
    const maxSize = maxFileSizeMb * 1024 * 1024;

    if (files.length > maxFiles) {
      return { valid: false, message: `最多可上傳 ${maxFiles} 張圖片。` };
    }

    for (const file of files) {
      if (!allowedTypes.has(file.type)) {
        return { valid: false, message: '檔案格式需為 JPG、PNG、WebP、HEIC 或 HEIF。' };
      }

      if (file.size > maxSize) {
        return { valid: false, message: '單張圖片建議 10MB 以內，請更換較小的檔案。' };
      }
    }

    return { valid: true };
  }

  async function uploadFileViaWorker(file, endpoint, uploadBatchId) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploadBatchId', uploadBatchId);

    const response = await fetch(endpoint, {
      method: 'POST',
      body: formData,
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Upload failed');
    }

    return data.referenceUrl || data.objectKey;
  }

  async function handleUpload(options) {
    const {
      files,
      input,
      preview,
      status,
      error,
      urlsInput,
      stateInput,
      filenamesInput,
      countInput,
      maxFiles,
      maxFileSizeMb,
      uploadMode,
      endpoint,
      uploadBatchId,
    } = options;

    clearError(error);
    clearPreview(preview);

    const validation = validateFiles(files, maxFiles, maxFileSizeMb);

    if (!validation.valid) {
      input.value = '';
      setFallbackState(urlsInput, stateInput, filenamesInput, countInput);
      status.textContent = fallbackState;
      showError(error, validation.message);
      return;
    }

    if (files.length === 0) {
      setFallbackState(urlsInput, stateInput, filenamesInput, countInput);
      status.textContent = fallbackState;
      return;
    }

    renderPreview(preview, files);
    filenamesInput.value = files.map((file) => file.name).join('\n');
    countInput.value = String(files.length);

    if (uploadMode === 'mock') {
      const mockUrls = files.map(createMockUrl);
      urlsInput.value = mockUrls.join('\n');
      stateInput.value = '已上傳';
      status.textContent = `已選擇 ${files.length} 張圖片。Mock mode 已寫入測試圖片連結。`;
      return;
    }

    if (isPlaceholderEndpoint(endpoint)) {
      setFailedState(urlsInput, stateInput, filenamesInput, countInput, files);
      status.textContent = failedState;
      showError(error, uploadErrorMessage);
      return;
    }

    try {
      status.textContent = `圖片上傳中，請稍候。0 / ${files.length}`;
      const uploadedUrls = [];

      for (let index = 0; index < files.length; index += 1) {
        const uploadedUrl = await uploadFileViaWorker(files[index], endpoint, uploadBatchId);
        if (!uploadedUrl) {
          throw new Error('Worker response did not include a referenceUrl or objectKey.');
        }
        uploadedUrls.push(uploadedUrl);
        status.textContent = `圖片上傳中，請稍候。${index + 1} / ${files.length}`;
      }

      urlsInput.value = uploadedUrls.join('\n');
      stateInput.value = '已上傳';
      status.textContent = '圖片已上傳完成';
    } catch (errorEvent) {
      console.error(errorEvent);
      setFailedState(urlsInput, stateInput, filenamesInput, countInput, files);
      status.textContent = failedState;
      showError(error, uploadErrorMessage);
    }
  }

  function createMockUrl(file) {
    const safeName = file.name
      .toLowerCase()
      .replace(/\.[^.]+$/, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-{2,}/g, '-');
    const extension = getExtension(file.name) || 'jpg';
    const timestamp = Date.now();
    return `https://example-upload.local/custom-silver/${timestamp}-${safeName || 'uploaded-image'}.${extension}`;
  }

  function getExtension(filename) {
    const match = String(filename).toLowerCase().match(/\.([a-z0-9]+)$/);
    return match ? match[1] : '';
  }

  function createUploadBatchId() {
    const now = new Date();
    const date = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
    ].join('');
    const time = [
      String(now.getHours()).padStart(2, '0'),
      String(now.getMinutes()).padStart(2, '0'),
      String(now.getSeconds()).padStart(2, '0'),
    ].join('');
    const random = createRandomToken(6);

    return `upload_${date}_${time}_${random}`;
  }

  function createRandomToken(length) {
    const bytes = new Uint8Array(length);

    if (window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, (byte) => (byte % 36).toString(36)).join('');
    }

    return Math.random().toString(36).slice(2, 2 + length).padEnd(length, '0');
  }

  function renderPreview(preview, files) {
    const fragment = document.createDocumentFragment();

    files.forEach((file, index) => {
      const item = document.createElement('figure');
      item.className = 'custom-silver-upload__preview-item';

      if (file.type !== 'image/heic' && file.type !== 'image/heif') {
        const image = document.createElement('img');
        image.alt = `已選擇圖片 ${index + 1}`;
        image.loading = 'lazy';
        image.src = URL.createObjectURL(file);
        image.addEventListener('load', () => URL.revokeObjectURL(image.src), { once: true });
        item.append(image);
      } else {
        const placeholder = document.createElement('div');
        placeholder.className = 'custom-silver-upload__preview-fallback';
        placeholder.textContent = 'HEIC';
        item.append(placeholder);
      }

      const caption = document.createElement('figcaption');
      caption.textContent = file.name;
      item.append(caption);
      fragment.append(item);
    });

    preview.append(fragment);
  }

  function clearPreview(preview) {
    preview.replaceChildren();
  }

  function clearError(error) {
    error.textContent = '';
    error.hidden = true;
  }

  function showError(error, message) {
    error.textContent = message;
    error.hidden = false;
  }

  function setFallbackState(urlsInput, stateInput, filenamesInput, countInput) {
    urlsInput.value = '';
    stateInput.value = fallbackState;
    filenamesInput.value = '';
    countInput.value = '0';
  }

  function setFailedState(urlsInput, stateInput, filenamesInput, countInput, files) {
    urlsInput.value = '';
    stateInput.value = failedState;
    filenamesInput.value = files.map((file) => file.name).join('\n');
    countInput.value = String(files.length);
  }

  function isPlaceholderEndpoint(endpoint) {
    return !endpoint || endpoint.includes('<ACCOUNT_SUBDOMAIN>');
  }
})();
